import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User, Provider } from "@supabase/supabase-js";
import type { UserPreferences } from "@veltro/shared/types";
import { getSupabase } from "@veltro/shared/lib/supabase";
import { UserPreferencesService } from "@veltro/shared/services";
import {
  getPendingAction,
  clearPendingSignup,
} from "@/shared/hooks/useSignupRedemption";
import { SUPABASE_URL } from "@/config/env";
import { logger } from "@/shared/utils/logger";

export interface UserProfile {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string | null;
  avatarUrl: string | null;
}

export interface TenantInfo {
  name: string | null;
  slug: string | null;
  status: string | null;
  settings: Record<string, unknown> | null;
  featureFlags: import("@veltro/shared/types").FeatureFlags | null;
}

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  tenant: TenantInfo | null;
  preferences: UserPreferences | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isProvisioningAccount: boolean;
  provisioningFailed: boolean;
  provisioningError: string | null;
  profileMissing: boolean;
  tenantId: string | null;
  userRole: string | null;
  licenseType: string | null;
  isSystemAdmin: boolean;
  isEmailVerified: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signInWithOAuth: (provider: Provider) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resendVerificationEmail: () => Promise<{ error?: string }>;
  retryProvisioning: () => Promise<void>;
  clearProvisioningError: () => void;
  updatePreferences: (fields: Partial<Pick<UserPreferences, "locale" | "onboarding" | "dismissedBanners">>) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

function extractClaim(session: Session | null, claim: string): string | null {
  if (!session?.access_token) return null;
  try {
    const payload = JSON.parse(atob(session.access_token.split(".")[1]));
    return payload[claim] ?? null;
  } catch {
    return null;
  }
}

function extractBoolClaim(session: Session | null, claim: string): boolean {
  if (!session?.access_token) return false;
  try {
    const payload = JSON.parse(atob(session.access_token.split(".")[1]));
    return payload[claim] === true;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProvisioningAccount, setIsProvisioningAccount] = useState(false);
  const [provisioningFailed, setProvisioningFailed] = useState(false);
  const [provisioningError, setProvisioningError] = useState<string | null>(null);
  const [profileMissing, setProfileMissing] = useState(false);

  // Stable ref — getSupabase() returns the same singleton
  const supabaseRef = useRef(getSupabase());
  // Prevent processing pending signup more than once
  const pendingProcessedRef = useRef(false);
  // Prevent processing metadata-based provisioning more than once
  const metadataProvisioningRef = useRef(false);
  // Prevent concurrent loadMetadata calls (race between getSession and onAuthStateChange)
  const loadingMetadataRef = useRef(false);

  // Provision trial from user_metadata (stored during signup before email confirmation).
  // This handles the case where the user signed up, confirmed email, and now logs in
  // — the signup form data is in user_metadata.pending_trial.
  const provisionFromMetadata = useCallback(async (s: Session): Promise<boolean> => {
    if (metadataProvisioningRef.current) return false;
    const meta = s.user.user_metadata;
    if (!meta?.pending_trial || !meta?.company_name) return false;

    metadataProvisioningRef.current = true;
    setIsProvisioningAccount(true);
    setProvisioningFailed(false);
    setProvisioningError(null);

    try {
      // Get the freshest token from the Supabase client's internal session.
      // The client auto-refreshes tokens (autoRefreshToken: true), so getSession()
      // returns the current valid token. Avoid refreshSession() here because it
      // can race with auto-refresh and invalidate both tokens.
      const sb = supabaseRef.current;
      if (!sb) throw new Error("Supabase not initialized");

      let token: string | undefined;
      const { data: { session: current } } = await sb.auth.getSession();
      if (current?.access_token) {
        token = current.access_token;
      } else {
        // Last resort: force refresh if getSession returned nothing
        const { data: { session: refreshed } } = await sb.auth.refreshSession();
        token = refreshed?.access_token;
      }
      if (!token) throw new Error("No valid session token");

      const res = await fetch(`${SUPABASE_URL}/functions/v1/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "trial",
          companyName: meta.company_name,
          firstName: meta.first_name || "",
          lastName: meta.last_name || "",
          locale: meta.locale || "en",
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // Clear the pending_trial flag from metadata so it doesn't retry
        const sb = supabaseRef.current;
        if (sb) {
          await sb.auth.updateUser({ data: { pending_trial: null } });
          await sb.auth.refreshSession();
        }
        return true;
      }
      // Non-OK response — provisioning failed
      const errMsg = data.error || `HTTP ${res.status}`;
      logger.error("[AuthProvider] provisionFromMetadata failed:", errMsg);
      setProvisioningError(errMsg);
      setProvisioningFailed(true);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Network error";
      logger.error("[AuthProvider] provisionFromMetadata exception:", err);
      setProvisioningError(errMsg);
      setProvisioningFailed(true);
    } finally {
      setIsProvisioningAccount(false);
      metadataProvisioningRef.current = false;
    }
    return false;
  }, []);

  // Load profile and tenant data for a given session (non-blocking enrichment).
  const loadMetadata = useCallback(async (s: Session | null) => {
    const sb = supabaseRef.current;
    if (!sb || !s?.user) {
      setProfile(null);
      setTenant(null);
      setPreferences(null);
      setProfileMissing(false);
      return;
    }

    // Prevent concurrent loadMetadata calls — both getSession().then() and
    // onAuthStateChange("SIGNED_IN") can trigger this simultaneously.
    if (loadingMetadataRef.current) return;
    loadingMetadataRef.current = true;

    const tid = extractClaim(s, "tenant_id");

    try {
      const [profileRes, tenantRes, prefs] = await Promise.all([
        sb
          .from("profiles")
          .select("first_name, last_name, email, role, avatar_url")
          .eq("id", s.user.id)
          .single(),
        tid
          ? sb
              .from("tenants")
              .select("name, slug, status, settings, feature_flags")
              .eq("id", tid)
              .single()
          : Promise.resolve({ data: null, error: null }),
        UserPreferencesService.get(),
      ]);

      if (profileRes.data) {
        setProfile({
          firstName: profileRes.data.first_name,
          lastName: profileRes.data.last_name,
          email: profileRes.data.email,
          role: profileRes.data.role,
          avatarUrl: profileRes.data.avatar_url,
        });
        setProfileMissing(false);
      } else if (profileRes.error && profileRes.error.code === "PGRST116") {
        // PGRST116 = "no rows returned" from .single() — profile truly missing.
        // Check if user has pending_trial metadata — if so, auto-provision.
        const meta = s.user.user_metadata;
        if (meta?.pending_trial && meta?.company_name) {
          const provisioned = await provisionFromMetadata(s);
          if (provisioned) {
            // Re-fetch fresh session and reload metadata with new tenant_id
            const { data: { session: fresh } } = await sb.auth.getSession();
            if (fresh) {
              setSession(fresh);
              setUser(fresh.user ?? null);
              // Allow recursive call by resetting the guard
              loadingMetadataRef.current = false;
              await loadMetadata(fresh);
              return;
            }
          }
          // If provisionFromMetadata returned false because another call is
          // already provisioning (metadataProvisioningRef guard), do NOT set
          // profileMissing — the other call will handle it.
          if (metadataProvisioningRef.current) return;
        }
        setProfile(null);
        setProfileMissing(true);
      } else {
        // Query error (RLS, network, etc.) — don't flag as missing, just clear
        setProfile(null);
        setProfileMissing(false);
      }

      if (tenantRes.data) {
        setTenant({
          name: tenantRes.data.name,
          slug: tenantRes.data.slug,
          status: tenantRes.data.status,
          settings: tenantRes.data.settings as Record<string, unknown> | null,
          featureFlags: tenantRes.data.feature_flags as import("@veltro/shared/types").FeatureFlags | null,
        });
      } else {
        setTenant(null);
      }

      setPreferences(prefs);
    } catch {
      // Network/unexpected error — don't flag profileMissing, just clear state
      setProfile(null);
      setTenant(null);
      setPreferences(null);
      setProfileMissing(false);
    } finally {
      loadingMetadataRef.current = false;
    }
  }, [provisionFromMetadata]);

  // Process any pending signup/invite from sessionStorage (after OAuth redirect)
  const processPendingSignup = useCallback(async (s: Session) => {
    if (pendingProcessedRef.current) return;
    const pending = getPendingAction();
    if (!pending) return;

    pendingProcessedRef.current = true;
    setIsProvisioningAccount(true);

    try {
      // Use the client's current session token (may have been auto-refreshed)
      const sb = supabaseRef.current;
      let token = s.access_token;
      if (sb) {
        const { data: { session: current } } = await sb.auth.getSession();
        if (current?.access_token) token = current.access_token;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(pending),
      });

      const data = await res.json();
      clearPendingSignup();

      if (res.ok && data.success) {
        // Refresh session so JWT picks up new tenant_id claim
        const sb = supabaseRef.current;
        if (sb) {
          await sb.auth.refreshSession();
        }
      }
    } catch {
      clearPendingSignup();
    } finally {
      setIsProvisioningAccount(false);
    }
  }, []);

  useEffect(() => {
    const supabase = supabaseRef.current;

    // Phase A mock mode — no Supabase configured, skip auth
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    // Get initial session — resolve isLoading immediately, load metadata in background
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setIsLoading(false);

      // Background: process pending signup + load profile/tenant metadata
      if (s) {
        processPendingSignup(s).then(async () => {
          const { data: { session: fresh } } = await supabase.auth.getSession();
          if (fresh) {
            setSession(fresh);
            setUser(fresh.user ?? null);
          }
          await loadMetadata(fresh ?? s);
        }).catch(() => {});
      }
    });

    // Listen for auth changes — skip INITIAL_SESSION (already handled above)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (event === "INITIAL_SESSION") return;
      // Skip token refresh events — they don't need metadata reload and cause
      // race conditions with in-progress provisioning.
      if (event === "TOKEN_REFRESHED") return;
      // USER_UPDATED — just update user ref, no metadata reload needed
      if (event === "USER_UPDATED") {
        setUser(s?.user ?? null);
        return;
      }

      setSession(s);
      setUser(s?.user ?? null);
      setIsLoading(false);

      // PASSWORD_RECOVERY — redirect to the reset-password page
      if (event === "PASSWORD_RECOVERY") {
        window.location.hash = "#/reset-password";
        return;
      }

      if (event === "SIGNED_IN" && s) {
        processPendingSignup(s).then(async () => {
          const { data: { session: fresh } } = await supabase.auth.getSession();
          if (fresh) {
            setSession(fresh);
            setUser(fresh.user ?? null);
          }
          await loadMetadata(fresh ?? s);
        }).catch(() => {});
        return;
      }

      if (s) {
        loadMetadata(s).catch(() => {});
      }
    });

    return () => subscription.unsubscribe();
  }, [loadMetadata, processPendingSignup]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error?: string }> => {
      const supabase = supabaseRef.current;
      if (!supabase) return { error: "Supabase not configured" };
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) return { error: error.message };
      return {};
    },
    []
  );

  const signInWithOAuth = useCallback(
    async (provider: Provider): Promise<{ error?: string }> => {
      const supabase = supabaseRef.current;
      if (!supabase) return { error: "Supabase not configured" };
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + window.location.pathname,
        },
      });
      if (error) return { error: error.message };
      return {};
    },
    []
  );

  const signOut = useCallback(async () => {
    const supabase = supabaseRef.current;
    if (!supabase) return;
    setProfileMissing(false);
    await supabase.auth.signOut();
  }, []);

  const resendVerificationEmail = useCallback(async (): Promise<{ error?: string }> => {
    if (!user?.email) return { error: "No email address found." };
    const supabase = getSupabase();
    if (!supabase) return { error: "Supabase client not initialized." };
    const { error } = await supabase.auth.resend({ type: "signup", email: user.email });
    if (error) return { error: error.message };
    return {};
  }, [user?.email]);

  const clearProvisioningError = useCallback(() => {
    setProvisioningError(null);
    setProvisioningFailed(false);
  }, []);

  const retryProvisioning = useCallback(async () => {
    const sb = supabaseRef.current;
    if (!sb) return;
    metadataProvisioningRef.current = false;
    const { data: { session: s } } = await sb.auth.getSession();
    if (s) {
      const provisioned = await provisionFromMetadata(s);
      if (provisioned) {
        const { data: { session: fresh } } = await sb.auth.getSession();
        if (fresh) {
          setSession(fresh);
          setUser(fresh.user ?? null);
          await loadMetadata(fresh);
        }
      }
    }
  }, [provisionFromMetadata, loadMetadata]);

  const updatePreferences = useCallback(
    async (fields: Partial<Pick<UserPreferences, "locale" | "onboarding" | "dismissedBanners">>) => {
      await UserPreferencesService.update(fields);
      // Optimistically update local state
      setPreferences((prev) => prev ? { ...prev, ...fields } : prev);
    },
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      tenant,
      preferences,
      isLoading,
      isAuthenticated: !supabaseRef.current || !!session,
      isProvisioningAccount,
      provisioningFailed,
      provisioningError,
      profileMissing,
      tenantId: extractClaim(session, "tenant_id"),
      userRole: extractClaim(session, "user_role"),
      licenseType: extractClaim(session, "license_type"),
      isSystemAdmin: extractBoolClaim(session, "is_system_admin"),
      isEmailVerified: !!user?.email_confirmed_at,
      signIn,
      signInWithOAuth,
      signOut,
      resendVerificationEmail,
      retryProvisioning,
      clearProvisioningError,
      updatePreferences,
    }),
    [user, session, profile, tenant, preferences, isLoading, isProvisioningAccount, provisioningFailed, provisioningError, profileMissing, signIn, signInWithOAuth, signOut, resendVerificationEmail, retryProvisioning, clearProvisioningError, updatePreferences]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
